import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { useLocation, useHistory } from 'react-router-dom';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import Notification from '@this/src/notification';
import TripAiMessagesContainer from './components/TripAiMessagesContainer';
import { useTrip } from './hooks/useTrip';
import { sendAiMessage, fetchProposalBundles, rejectProposalBundle, selectProposalItem } from './api';
import type { ProposalBundle } from './api';
import type { ChatMessage } from './constants/mocks/messages';
import type { UserInfo } from './types/user';
import type { ProposalNotification } from './hooks/useAiMessages';

type RouteParams = {
  id: string;
};

type LocationState = {
  initialMessage?: string;
};

interface Props extends RouteComponentProps<RouteParams> {
  user: UserInfo;
}

const TripAiMessagesPage: React.FC<Props> = ({ match, user }) => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { trip, pusherInfo, loading, error } = useTrip(match.params.id);
  const tripId = match.params.id;

  const [proposalNotification, setProposalNotification] = useState<ProposalNotification | null>(null);
  const [proposalBundles, setProposalBundles] = useState<ProposalBundle[]>([]);

  const handleProposalReceived = useCallback((notification: ProposalNotification) => {
    setProposalNotification(notification);
  }, []);

  const handleSelectItem = useCallback(
    async (bundleId: number, itemId: number) => {
      const targetBundle = proposalBundles.find(bundle => bundle.id === bundleId);
      if (!targetBundle) {
        return;
      }
      const targetItem = targetBundle.items.find(item => item.id === itemId);
      if (!targetItem || targetItem.selected) {
        return;
      }

      const previousBundles = proposalBundles;
      setProposalBundles(prev =>
        prev.map(bundle => {
          if (bundle.id !== bundleId) return bundle;
          return {
            ...bundle,
            state: 'selected' as const,
            items: bundle.items.map(item => ({
              ...item,
              selected: item.id === itemId
            }))
          };
        })
      );

      try {
        await selectProposalItem(tripId, bundleId, itemId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[TripAiMessagesPage] Failed to select item:', e);
        setProposalBundles(previousBundles);
      }
    },
    [tripId, proposalBundles]
  );

  const handleCloseBundle = useCallback(
    async (bundleId: number) => {
      const targetBundle = proposalBundles.find(bundle => bundle.id === bundleId);
      if (!targetBundle || targetBundle.state === 'rejected') {
        return;
      }

      const previousBundles = proposalBundles;
      setProposalBundles(prev =>
        prev.map(bundle => {
          if (bundle.id !== bundleId) return bundle;
          return {
            ...bundle,
            state: 'rejected' as const,
            items: bundle.items.map(item => ({
              ...item,
              selected: false
            }))
          };
        })
      );

      try {
        await rejectProposalBundle(tripId, bundleId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[TripAiMessagesPage] Failed to reject bundle:', e);
        setProposalBundles(previousBundles);
      }
    },
    [tripId, proposalBundles]
  );

  useEffect(() => {
    if (!proposalNotification || proposalNotification.bundleIds.length === 0) {
      return;
    }

    const loadBundles = async () => {
      try {
        const bundles = await fetchProposalBundles(tripId, proposalNotification.bundleIds);
        setProposalBundles(bundles);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[TripAiMessagesPage] Failed to fetch proposal bundles:', e);
      }
    };

    loadBundles();
  }, [proposalNotification, tripId]);

  useEffect(() => {
    if (proposalBundles.length > 0) {
      // eslint-disable-next-line no-console
      console.log('[TripAiMessagesPage] proposalBundles updated:', proposalBundles);
    }
  }, [proposalBundles]);

  const initialMessage = useMemo(() => {
    return location.state?.initialMessage;
  }, [location.state]);

  const initialMessages: ChatMessage[] = useMemo(() => {
    if (!initialMessage) return [];
    return [
      {
        id: `initial-${tripId}`,
        direction: 'user',
        text: initialMessage,
        createdAt: new Date().toISOString()
      },
      {
        id: `optimistic-${tripId}`,
        direction: 'ai',
        text: '応答を生成中です...',
        createdAt: new Date().toISOString(),
        type: 'optimistic'
      }
    ];
  }, [initialMessage, tripId]);

  const hasSentInitialRef = useRef(false);
  const prevTripIdRef = useRef(tripId);
  const prevInitialMessageRef = useRef(initialMessage);

  useEffect(() => {
    if (prevTripIdRef.current !== tripId || prevInitialMessageRef.current !== initialMessage) {
      hasSentInitialRef.current = false;
      prevTripIdRef.current = tripId;
      prevInitialMessageRef.current = initialMessage;
    }
  }, [tripId, initialMessage]);

  useEffect(() => {
    if (!initialMessage || hasSentInitialRef.current) return;
    if (!trip || !pusherInfo) return;

    hasSentInitialRef.current = true;
    const sendInitial = async () => {
      try {
        await sendAiMessage(tripId, initialMessage, []);
        history.replace(location.pathname, {});
      } catch {
        Notification.error('送信に失敗しました。時間をおいてもう一度お試しください。');
      }
    };
    sendInitial();
  }, [initialMessage, tripId, trip, pusherInfo, history, location.pathname]);

  if (error) {
    return <div style={{ padding: '24px' }}>{error}</div>;
  }

  const allowPartial = Boolean(initialMessage);
  if ((loading || !trip || !pusherInfo) && !allowPartial) {
    return <Loading />;
  }

  return (
    <TripAiMessagesContainer
      tripId={tripId}
      trip={trip || null}
      user={user}
      pusherKey={pusherInfo?.key || ''}
      pusherChannelPrefix={pusherInfo?.channelPrefix || ''}
      headerTitle={`旅程番号：${tripId}`}
      subtitle="AI予約モード"
      enableMessaging={!loading && !!trip && !!pusherInfo}
      initialMessages={initialMessages}
      onProposalReceived={handleProposalReceived}
      proposalBundles={proposalBundles}
      onSelectItem={handleSelectItem}
      onCloseItem={handleCloseBundle}
    />
  );
};

export default TripAiMessagesPage;
